<template>
  <v-dialog
    v-model="openSelection"
    class="bg-white dark-bg-custom"
    max-width="600px"
    persistent
  >
    <div class="bg-white dark-bg-custom px-5 py-5" v-if="!stateFetch">
      <div v-if="!isExportDialog">
        <p>Select Extra Columns</p>
        <v-select
          v-model="extraColumns"
          :items="options"
          multiple
          outlined
          dense
        >
        </v-select>
      </div>
      <div v-else>
        <p>Click continue to download</p>
      </div>
      <div class="flex justify-end gap-3">
        <v-btn outlined @click="cancel"> Cancel </v-btn>
        <v-btn
          :color="$vuetify.theme.currentTheme.primary"
          class="white--text"
          @click="printData"
        >
          Continue
        </v-btn>
      </div>
    </div>
    <v-container class="bg-white dark-bg-custom" style="height: 200px" v-else>
      <v-row class="fill-height" align-content="center" justify="center">
        <v-col class="text-subtitle-1 text-center" cols="12">
          Preparing Data...
        </v-col>
        <v-col cols="6">
          <v-progress-linear
            color="black"
            indeterminate
            rounded
            height="6"
          ></v-progress-linear>
        </v-col>
      </v-row>
    </v-container>
  </v-dialog>
</template>

<script>
import * as XLSX from "xlsx";
import { sortDropdownOptions } from "~/utils/common";

export default {
  data() {
    return {
      options: [
        "Origin Action",
        "Destination Action",
        "Origin",
        "Destination",
        "Distance B/W Origin Destination(in Km)",
      ],
      extraColumns: [],
      openSelection: false,
      formattedData: [],
      isExportDialog: false,
    };
  },
  props: {
    items: Array,
    currentMonthDates: Array,
    selectedColumns: Array,
    startDate: String,
    endDate: String,
    stateFetch: Boolean,
  },
  methods: {
    exportToExcel() {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);
      const sortedItems = sortDropdownOptions([...this.items], "name");

      // Create main header row with dates
      const mainHeader = ["Name", "Employee Id", "Reporting To"];
      this.currentMonthDates.forEach((date) => {
        let colSpan = this.selectedColumns.length;
        mainHeader.push({
          v: date,
          t: "s",
          s: { alignment: { horizontal: "center", vertical: "center" } },
        });

        for (let i = 1; i < colSpan; i++) {
          mainHeader.push(null);
        }
      });

      XLSX.utils.sheet_add_aoa(ws, [mainHeader]);

      // Merge the main headers
      this.currentMonthDates.forEach((date, index) => {
        let colSpan = this.selectedColumns.length;
        const start = index * colSpan + 3;
        const end = start + colSpan - 1;
        ws["!merges"] = ws["!merges"] || [];
        ws["!merges"].push({ s: { r: 0, c: start }, e: { r: 0, c: end } });
      });

      // Create sub-header row with selected columns
      const subHeader = ["", "", ""];
      this.currentMonthDates.forEach(() => {
        this.selectedColumns.forEach((column) => {
          if (column !== "Clock-In Image" && column !== "Clock-Out Image") {
            subHeader.push(column);
          }
        });
        if (this.selectedColumns.includes("Clock-In Image")) {
          subHeader.push("Clock-In Image");
        }
        if (this.selectedColumns.includes("Clock-Out Image")) {
          subHeader.push("Clock-Out Image");
        }
      });
      subHeader.push(
        "Total Absent",
        "Total Present",
        "Total Week-off",
        "Total Miss punch",
        "Total Leaves",
        "Clock In (in %)"
      );

      XLSX.utils.sheet_add_aoa(ws, [subHeader], { origin: -1 });

      // Add data to worksheet
      sortedItems.forEach((item) => {
        const row = [item.name, item.orgEmployeeId, item?.reporteeName];
        let totalAbsent = 0;
        let totalPresent = 0;
        let totalWeekOff = 0;
        let totalMissPunch = 0;
        let totalLeaves = 0;
        let daysCount = 0;
        this.currentMonthDates.forEach((date) => {
          daysCount++;
          this.selectedColumns.forEach((column) => {
            if (column !== "Clock-In Image" && column !== "Clock-Out Image") {
              const value = item.attendance[date]?.[column] || "";
              row.push(value);

              if (value === "ABSENT") totalAbsent++;
              if (value === "PRESENT") totalPresent++;
              if (value === "WEEK_OFF") totalWeekOff++;
              if (value === "LEAVE") totalLeaves++;
              if (value === "MISS_PUNCH") totalMissPunch++;
            }
          });
          if (this.selectedColumns.includes("Clock-In Image")) {
            row.push(item.attendance[date]?.clockInImage || "-");
          }
          if (this.selectedColumns.includes("Clock-Out Image")) {
            row.push(item.attendance[date]?.clockOutImage || "-");
          }
        });
        const clockInPercentage = `${((totalPresent * 100) / daysCount).toFixed(
          2
        )} %`;

        row.push(
          totalAbsent,
          totalPresent,
          totalWeekOff,
          totalMissPunch,
          totalLeaves,
          clockInPercentage
        );
        XLSX.utils.sheet_add_aoa(ws, [row], { origin: -1 });
      });
      XLSX.utils.book_append_sheet(wb, ws, "Attendance");
      XLSX.writeFile(
        wb,
        `Attendance_${this.$props?.startDate}_${this.$props?.endDate}.xlsx`
      );
    },
    formatData() {
      const formattedData = [];
      const sortedItems = sortDropdownOptions(this.items, "name");

      let serialNumber = 1;
      sortedItems.forEach((employee) => {
        let totalDistance = 0;
        let showTotalDistance = this.selectedColumns?.includes("Distance (km)");

        this.currentMonthDates.forEach((date) => {
          const attendance = employee.attendance[date] || {};
          if (showTotalDistance && Number(attendance["Distance (km)"])) {
            totalDistance += Number(attendance["Distance (km)"]);
          }
        });
        this.currentMonthDates.forEach((date, index) => {
          const newDate = date + this.$dayjs(this.startDate).format("YYYY");
          const formattedDate = this.$dayjs(newDate).format("DD/MM/YYYY");
          let isFirstPairForDate = true;

          const attendance = employee.attendance[date] || {};
          const reporteeName = employee?.reporteeName
            ? employee.reporteeName
            : "N/A";
          const baseRow = [
            index === 0 ? serialNumber : "",
            index === 0 ? `${employee.name} - ${employee.mobile}` : "",
            index === 0 ? employee.orgEmployeeId : "",
            index === 0 ? reporteeName : "",
            formattedDate,
            attendance.attendance || "-",
            ...this.selectedColumns.map((col) => attendance[col] || "-"),
          ];

          if (
            Array.isArray(attendance.pairDistance) &&
            this.extraColumns.length
          ) {
            attendance.pairDistance.forEach((pair, i) => {
              const pairRow = [
                isFirstPairForDate ? baseRow[0] : "",
                isFirstPairForDate ? baseRow[1] : "",
                isFirstPairForDate ? baseRow[2] : "",
                isFirstPairForDate ? baseRow[3] : "",
                isFirstPairForDate ? baseRow[4] : "",
                ...(isFirstPairForDate
                  ? baseRow.slice(5)
                  : Array(this.selectedColumns.length).fill("")),
                ...this.extraColumns.map((col) => {
                  switch (col) {
                    case "Origin Action": {
                      return pair.originAction || "-";
                    }
                    case "Destination Action": {
                      return pair.destinationAction || "-";
                    }
                    case "Origin": {
                      return pair.origin || "-";
                    }
                    case "Destination": {
                      return pair.destination || "-";
                    }
                    case "Distance B/W Origin Destination(in Km)": {
                      const distanceKey = `pair_${i + 1}`;
                      return pair[distanceKey] !== undefined
                        ? `${pair[distanceKey]}`
                        : "-";
                    }
                    default: {
                      return "-";
                    }
                  }
                }),
                index === 0 && i === 0 && showTotalDistance
                  ? `Total distance: ${totalDistance?.toFixed(2)} km`
                  : "",
              ];
              formattedData.push(pairRow);
              isFirstPairForDate = false;
            });
          } else {
            const rowWithoutPairs = [
              baseRow[0],
              baseRow[1],
              baseRow[2],
              baseRow[3],
              baseRow[4],
              ...baseRow.slice(5),
              ...this.extraColumns.map(() => "-"),
              index === 0 && showTotalDistance
                ? `Total distance: ${totalDistance?.toFixed(2)} km`
                : "",
            ];
            formattedData.push(rowWithoutPairs);
          }
        });
        formattedData.push([]);
        serialNumber++;
      });
      return formattedData;
    },
    openDialog() {
      this.openSelection = true;
    },
    openExportDialog() {
      this.openSelection = true;
      this.isExportDialog = true;
    },
    printData() {
      if (this.isExportDialog) {
        this.exportToExcel();
      } else {
        this.formattedData = this.formatData();
        this.exportFormattedExcel();
      }
      this.cancel();
      this.openSelection = false;
      this.isExportDialog = false;
    },
    cancel() {
      this.$emit("cancel");
      this.openSelection = false;
      this.isExportDialog = false;
    },
    exportFormattedExcel() {
      const headers = [
        "Sr.No",
        "Employee Name",
        "Employee Id",
        "Reporting To",
        "Date",
        "Attendance",
        ...this.selectedColumns,
        ...this.extraColumns,
      ];

      const ws = XLSX.utils.aoa_to_sheet([headers, ...this.formattedData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Attendance Data");

      XLSX.writeFile(
        wb,
        `AttendanceData_${this.$props?.startDate}_${this.$props?.endDate}.xlsx`
      );
    },
  },
  mounted() {
    this.$root.$on("export-excel", this.openExportDialog);
    this.$root.$on("export-format-excel", this.openDialog);
  },
  beforeDestroy() {
    this.$root.$off("export-excel", this.openExportDialog);
    this.$root.$off("export-format-excel", this.openDialog);
  },
};
</script>
